import { isAxiosError } from 'axios';

/**
 * Pulls `errors` out of a response body.
 *
 * The CSV endpoints ask for `responseType: 'text'`, which switches off axios'
 * JSON parsing, so their error body arrives as an unparsed string.
 */
const unwrapErrors = (data: unknown): unknown => {
  if (typeof data === 'string') {
    try {
      return (JSON.parse(data) as { errors?: unknown } | null)?.errors;
    } catch {
      return undefined;
    }
  }
  return (data as { errors?: unknown } | null | undefined)?.errors;
};

/**
 * Message to show the user for a failed request.
 *
 * The API returns `{ errors }` as either a string or an array of them depending
 * on the endpoint, so both are unwrapped here. Falls back to the supplied text
 * when the body carries nothing renderable — some endpoints put an object there,
 * and antd cannot render one.
 */
export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  // Not `instanceof`: a duplicated axios in the bundle gives a second AxiosError
  // class, and the server message would be dropped for anything built against it.
  if (!isAxiosError(error)) return fallback;

  const errors = unwrapErrors(error.response?.data);
  const message = Array.isArray(errors) ? errors[0] : errors;

  return typeof message === 'string' && message ? message : fallback;
};
